import { Event, EventSourcePolyfill } from 'event-source-polyfill'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChatStreamRequest, getChatStreamerEvent } from '../../api/modules/chatApi'
import { useAuth } from '../../context/AuthContext'
import { Agent } from '../../types/agentTypes'
import { KnowledgeSource, Reference, Flag } from '../../types/chatTypes'

interface ErrorEvent extends Event {
  type: 'error'
  status: number
  statusText: string
  error?: undefined
}

interface MessageEvent {
  message: string
  response_source?: 'internal_knowledge' | 'agent_llm'
}

interface MessageInfoEvent {
  message_id: string
  knowledge_source: KnowledgeSource
  references?: Reference[]
  response_source?: 'internal_knowledge' | 'agent_llm'
}

interface FlagEvent {
  flags: Flag[]
}

interface SourcesEvent {
  references?: Reference[]
}

// Mid-stream knowledge-source announcement (searching while a tool runs);
// the final message_info remains authoritative.
interface KnowledgeSourceEvent {
  knowledge_source: KnowledgeSource
  searching: boolean
}
interface ResponseTransitionEvent {
  response_source: 'internal_knowledge' | 'agent_llm'
  phase: 'internal_streaming' | 'llm_resumed'
}

interface DualResponse {
  internal_knowledge: string
  agent_llm: string
}

interface ServerErrorEvent {
  error_code?: string
  error_message?: string
  details?: {
    violations?: string[]
    validation_type?: string
    raw_error?: string
  }
}

export function useChatStream(
  chatStreamRequest: Omit<ChatStreamRequest, 'message'>,
  onMessageComplete: (
    message: string,
    tempId: string,
    messageId: string,
    knowledgeSource?: KnowledgeSource,
    references?: Reference[],
  ) => void,
  onStreamingUpdate: (
    partialMessage: string,
    tempId: string,
    messageId?: string,
    error?: Error,
    knowledgeSource?: KnowledgeSource,
    errorDetails?: { policy_violations?: string[] },
    responseSource?: 'internal_knowledge' | 'agent_llm',
    isSearchingKnowledge?: boolean,
  ) => void,
  onResponseTransition?: (
    tempId: string,
    responseSource: 'internal_knowledge' | 'agent_llm',
    phase: 'internal_streaming' | 'llm_resumed',
  ) => void,
  onFlagsReceived?: (flags: Flag[]) => void,
  // Fired when sources stream in ahead of the answer tokens, so the in-progress
  // answer can resolve inline citation markers live.
  onReferencesReceived?: (references: Reference[], tempId: string) => void,
) {
  const [streamingAnswer, setStreamingAnswer] = useState('')
  const [dualResponse, setDualResponse] = useState<DualResponse>({ internal_knowledge: '', agent_llm: '' })
  const [currentResponseSource, setCurrentResponseSource] = useState<'internal_knowledge' | 'agent_llm' | null>(null)
  const [newMessageId, setNewMessageId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { refreshUser } = useAuth()
  const [knowledgeSource, setKnowledgeSource] = useState<KnowledgeSource>('LLM_ONLY')
  const [references, setReferences] = useState<Reference[]>([])

  const streamingAnswerRef = useRef('')
  const dualResponseRef = useRef<DualResponse>({ internal_knowledge: '', agent_llm: '' })
  const currentResponseSourceRef = useRef<'internal_knowledge' | 'agent_llm' | null>(null)
  const knowledgeSourceRef = useRef<KnowledgeSource>('LLM_ONLY')
  const newMessageIdRef = useRef('')
  const referencesRef = useRef<Reference[]>([])
  const sourceRef = useRef<EventSourcePolyfill | null>(null)
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const STREAM_TIMEOUT_MS = 180000

  const setupEventSource = useCallback(
    async (
      requestPayload: ChatStreamRequest,
      tempId: string,
      isSecondary = false,
    ): Promise<EventSourcePolyfill | null> => {
      try {
        const source = await getChatStreamerEvent(requestPayload)
        if (!source) {
          throw new Error('Could not establish connection. Please try again.')
        }
        sourceRef.current = source

        source.onopen = () => {
          setIsLoading(false)

          // Start stream timeout — fires if no messages arrive within the timeout period
          if (streamTimeoutRef.current) clearTimeout(streamTimeoutRef.current)
          streamTimeoutRef.current = setTimeout(() => {
            const timeoutError = new Error('STREAM_TIMEOUT')
            setError(timeoutError)
            onStreamingUpdate('', tempId, undefined, timeoutError, undefined, undefined, undefined)
            setIsLoading(false)
            source.close()
            sourceRef.current = null
          }, STREAM_TIMEOUT_MS)
        }

        source.onmessage = event => {
          // Clear timeout on first message received
          if (streamTimeoutRef.current) {
            clearTimeout(streamTimeoutRef.current)
            streamTimeoutRef.current = null
          }
          const payload = JSON.parse(event.data) as MessageEvent

          // Route content based on response_source
          if (payload.response_source) {
            currentResponseSourceRef.current = payload.response_source
            setCurrentResponseSource(payload.response_source)

            dualResponseRef.current[payload.response_source] += payload.message
            setDualResponse({ ...dualResponseRef.current })

            // Pass the specific response content, not combined
            const responseContent = dualResponseRef.current[payload.response_source]
            onStreamingUpdate(
              responseContent,
              tempId,
              undefined,
              undefined,
              undefined,
              undefined,
              payload.response_source,
            )
          } else {
            // Fallback for single responses
            streamingAnswerRef.current += payload.message
            setStreamingAnswer(streamingAnswerRef.current)
            onStreamingUpdate(
              streamingAnswerRef.current,
              tempId,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
            )
          }
        }

        source.addEventListener('message_info', (event: any) => {
          const payload = JSON.parse(event.data) as MessageInfoEvent

          if (payload.references) {
            referencesRef.current = payload.references
            setReferences(payload.references)
          }

          newMessageIdRef.current = payload.message_id
          setNewMessageId(payload.message_id)
          knowledgeSourceRef.current = payload.knowledge_source
          setKnowledgeSource(payload.knowledge_source)

          // Pass the appropriate content based on response source
          const contentToUpdate = payload.response_source
            ? dualResponseRef.current[payload.response_source]
            : streamingAnswerRef.current

          onStreamingUpdate(
            contentToUpdate,
            tempId,
            isSecondary ? undefined : payload.message_id,
            undefined,
            isSecondary ? undefined : payload.knowledge_source,
            undefined,
            payload.response_source,
            false, // final envelope — any search is over
          )
        })

        source.addEventListener('response_transition', (event: any) => {
          const payload = JSON.parse(event.data) as ResponseTransitionEvent
          currentResponseSourceRef.current = payload.response_source
          setCurrentResponseSource(payload.response_source)

          // Notify parent component about the transition
          if (onResponseTransition) {
            onResponseTransition(tempId, payload.response_source, payload.phase)
          }
        })

        source.addEventListener('server-error', (event: any) => {
          const payload = JSON.parse(event.data) as ServerErrorEvent
          const errorCode = payload.error_code || 'server_error'
          const errorDetails = payload.details ? { policy_violations: payload.details.violations } : undefined

          let errorToSet: Error
          let messageToPreserve = ''
          const errorDetailsForAnswer: { policy_violations?: string[] } | undefined = errorDetails

          if (errorCode === 'USER_INPUT_CONTENT_VIOLATION') {
            errorToSet = new Error(errorCode)
          } else if (errorCode === 'BOT_RESPONSE_CONTENT_VIOLATION') {
            messageToPreserve = streamingAnswerRef.current
            errorToSet = new Error(errorCode)
          } else {
            // Handle all other server errors
            errorToSet = new Error(errorCode)
          }

          setError(errorToSet)
          // Pass the real message ID if available, so the error can be applied to the correct answer
          onStreamingUpdate(
            messageToPreserve,
            tempId,
            newMessageIdRef.current,
            errorToSet,
            undefined,
            errorDetailsForAnswer,
            undefined,
          )
          setIsLoading(false)

          source.close()
          sourceRef.current = null
        })


        source.addEventListener('flags', (event: any) => {
          const payload = JSON.parse(event.data) as FlagEvent
          if (payload.flags && onFlagsReceived) {
            onFlagsReceived(payload.flags)
          }
        })

        source.addEventListener('knowledge_source', (event: any) => {
          const payload = JSON.parse(event.data) as KnowledgeSourceEvent
          // Deliberately no knowledgeSourceRef write: the ref feeds the
          // onerror completion path and must only hold the authoritative
          // message_info value, never a provisional announcement that
          // _finalize may still downgrade.
          setKnowledgeSource(payload.knowledge_source)
          if (!isSecondary) {
            onStreamingUpdate(
              streamingAnswerRef.current,
              tempId,
              undefined,
              undefined,
              payload.knowledge_source,
              undefined,
              undefined,
              payload.searching,
            )
          }
        })

        source.addEventListener('sources', (event: any) => {
          const payload = JSON.parse(event.data) as SourcesEvent
          if (payload.references) {
            referencesRef.current = payload.references
            setReferences(payload.references)
            if (onReferencesReceived) {
              onReferencesReceived(payload.references, tempId)
            }
          }
        })

        source.onerror = error => {
          const errorEvent = error as ErrorEvent
          if (errorEvent && errorEvent.status) {
            let errorMessage = 'An unknown error occurred'
            if (errorEvent.status === 401) {
              refreshUser && refreshUser()
              errorMessage = `${errorEvent.statusText}. Probeer opnieuw`
            } else {
              errorMessage = `${errorEvent.statusText}. Contacteer support`
            }
            const backendError = new Error(errorMessage)
            setError(backendError)
            onStreamingUpdate('', tempId, undefined, backendError, undefined, undefined, undefined)
          } else if (errorEvent?.error) {
            const backendError = new Error(errorEvent?.error)
            setError(backendError)
            onStreamingUpdate('', tempId, undefined, backendError, undefined, undefined, undefined)
          }
          setIsLoading(false)

          // For dual responses, complete each response separately
          if (
            dualResponseRef.current.internal_knowledge.trim() !== '' ||
            dualResponseRef.current.agent_llm.trim() !== ''
          ) {
            // Complete internal knowledge response if it exists
            if (dualResponseRef.current.internal_knowledge.trim() !== '') {
              onMessageComplete(
                dualResponseRef.current.internal_knowledge,
                tempId,
                newMessageIdRef.current,
                'INTERNAL_KB',
                referencesRef.current,
              )
            }

            // Complete agent LLM response if it exists
            if (dualResponseRef.current.agent_llm.trim() !== '') {
              onMessageComplete(
                dualResponseRef.current.agent_llm,
                tempId,
                newMessageIdRef.current,
                knowledgeSourceRef.current || 'LLM_ONLY',
                referencesRef.current,
              )
            }
          } else if (streamingAnswerRef.current.trim() !== '') {
            // Fallback for single responses
            onMessageComplete(
              streamingAnswerRef.current,
              tempId,
              newMessageIdRef.current,
              knowledgeSourceRef.current,
              referencesRef.current,
            )
          }

          setStreamingAnswer('')
          source.close()
        }

        return source
      } catch (error) {
        setError(error as Error)
        setIsLoading(false)
        onStreamingUpdate('', tempId, undefined, error as Error, undefined, undefined, undefined)
        return null
      }
    },
    [
      refreshUser,
      onMessageComplete,
      onStreamingUpdate,
      onResponseTransition,
      onFlagsReceived,
      onReferencesReceived,
    ],
  )

  const sendMessage = useCallback(
    async (
      message: string,
      tempId: string,
      agent?: Agent,
      assetId?: string,
      addiontalPayload?: Partial<ChatStreamRequest>,
    ) => {
      try {
        setIsLoading(true)
        setError(null)
        setStreamingAnswer('')
        streamingAnswerRef.current = ''
        setReferences([])
        referencesRef.current = []

        // Reset dual response objects
        const initialDualResponse = { internal_knowledge: '', agent_llm: '' }
        setDualResponse(initialDualResponse)
        dualResponseRef.current = initialDualResponse
        setCurrentResponseSource(null)
        currentResponseSourceRef.current = null

        // Close any existing streams
        if (sourceRef.current) {
          sourceRef.current.close()
          sourceRef.current = null
        }

        const requestPayload: ChatStreamRequest = {
          ...chatStreamRequest,
          ...addiontalPayload,
          message,
        }

        // Agent can be absent for existing chats
        if (agent) {
          requestPayload.agentId = agent.id
        }

        // Add fileIds to the request payload if documentId is provided
        if (assetId) {
          requestPayload.fileIds = [assetId]
        }

        // Remove different handling based on selectedTool; always create a single stream per call
        const source = await setupEventSource(requestPayload, tempId)
        if (source) {
          sourceRef.current = source
        } else {
          throw new Error('Could not establish connection. Please try again.')
        }
      } catch (error) {
        setError(error as Error)
        setIsLoading(false)
        onStreamingUpdate('', tempId, undefined, error as Error, undefined, undefined, undefined)

        // Close any streams that might have been opened
        if (sourceRef.current) {
          sourceRef.current.close()
          sourceRef.current = null
        }
      }
    },
    [chatStreamRequest, setupEventSource, onStreamingUpdate],
  )

  // Abort an in-flight stream: close the EventSource and drop the loading
  // state. This stops the client consuming the response; the backend run is
  // cancelled separately (DVG-222).
  const stopStream = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current)
      streamTimeoutRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.close()
      sourceRef.current = null
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        sourceRef.current.close()
        sourceRef.current = null
      }
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current)
        streamTimeoutRef.current = null
      }
    }
  }, [])

  return {
    streamingAnswer,
    dualResponse,
    currentResponseSource,
    newMessageId,
    isLoading,
    error,
    knowledgeSource,
    references,
    sendMessage,
    stopStream,
  }
}
